// hooks/usePricingPlans.js
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { isAdminRole } from "../utils/userRoles";
import {
  PLAN_LIMITS,
  STRIPE_SUBSCRIPTION_PRICE_IDS,
  STRIPE_ONETIME_PRICE_IDS,
  VALID_PAID_PLANS,
} from "../config/billingPlans";

/** Тексты и видимость платных планов; ключ = имя плана (как в VALID_PAID_PLANS и metadata Stripe). */
const PAID_PLAN_UI = {
  Start: {
    label: "Starter",
    features: (limit) => [
      `${limit.toLocaleString()} emails/month`,
      "Premium templates",
      "Priority support",
      "Analytics",
    ],
    isVisible: () => true,
  },
  Business: {
    label: "Business",
    features: (limit) => [
      `${limit.toLocaleString()} emails/month`,
      "Advanced templates",
      "Premium support",
      "Advanced analytics",
      "API access",
    ],
    isVisible: () => false,
  },
  Enterprise: {
    label: "Enterprise",
    features: (limit) => [
      `${limit.toLocaleString()} emails/month`,
      "Custom templates",
      "Dedicated support",
      "Custom integrations",
      "Advanced reporting",
    ],
    isVisible: () => false,
  },
  Million: {
    label: "Million",
    features: (limit) => [
      `${limit.toLocaleString()} emails/month`,
      "Custom everything",
      "White-label",
      "Dedicated account manager",
      "Custom SLA",
    ],
    isVisible: () => false,
  },
};

// *** БАЗОВАЯ КОНФИГУРАЦИЯ ПЛАНОВ ***
const PLAN_CONFIGS = {
  Basic: {
    name: "Basic",
    basePrice: 0,
    subscriptionPrice: 0,
    limit: PLAN_LIMITS["Basic"],
    description:
      "Basic plan with " + PLAN_LIMITS["Basic"] + " emails per month",
    features: [
      `${PLAN_LIMITS.Basic.toLocaleString()} emails/month`,
      "Basic templates",
      "Email support",
    ],
    isVisible: () => true,
    requiresPayment: false,
  },
  ...Object.fromEntries(
    VALID_PAID_PLANS.map((planKey) => {
      const ui = PAID_PLAN_UI[planKey];
      const limit = PLAN_LIMITS[planKey];
      if (!ui) {
        throw new Error(
          `usePricingPlans: add PAID_PLAN_UI entry for "${planKey}"`,
        );
      }
      return [
        planKey,
        {
          name: planKey,
          // Суммы в евро только из /api/billing/stripe-prices; здесь не ID Stripe (не путать с ценой).
          basePrice: 0,
          subscriptionPrice: 0,
          stripeOneTimePriceId: STRIPE_ONETIME_PRICE_IDS[planKey],
          stripeSubscriptionPriceId: STRIPE_SUBSCRIPTION_PRICE_IDS[planKey],
          limit,
          description: `${ui.label} plan with ${limit.toLocaleString()} emails per month`,
          features: ui.features(limit),
          isVisible: ui.isVisible,
          requiresPayment: true,
        },
      ];
    }),
  ),
  Unlimited: {
    name: "Unlimited",
    basePrice: 0, // Нет покупки для админов
    subscriptionPrice: 0, // Нет подписки для админов
    limit: Infinity,
    description: "Unlimited plan with unlimited emails (admin only)",
    features: [
      "Unlimited emails",
      "All features",
      "Admin access",
      "No restrictions",
    ],
    isVisible: (user) => {
      return isAdminRole(user?.role);
    },
    requiresPayment: false,
  },
};

// *** ХРАНИЛИЩЕ АКТУАЛЬНЫХ ЦЕН ИЗ STRIPE ***
let stripePricesCache = null;
let stripePricesCacheTime = null;
const CACHE_DURATION = 2 * 60 * 1000; // 2 мин — быстрее подхватывать смену цен после деплоя/Stripe

// *** ОСНОВНОЙ ХУК УПРАВЛЕНИЯ ПЛАНАМИ ***
export const usePricingPlans = (user = null, totalSpent = 0) => {
  const [stripePrices, setStripePrices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // *** ЗАГРУЗКА ЦЕН ИЗ STRIPE ***
  const loadStripePrices = async () => {
    try {
      // Проверяем кэш
      const now = Date.now();
      if (
        stripePricesCache &&
        stripePricesCacheTime &&
        now - stripePricesCacheTime < CACHE_DURATION
      ) {
        setStripePrices(stripePricesCache);
        setLoading(false);
        return;
      }

      // Загружаем актуальные цены из Stripe
      const response = await axios.get("/api/billing/stripe-prices", {
        withCredentials: true,
      });
      const prices = response.data;

      // Обновляем кэш
      stripePricesCache = prices;
      stripePricesCacheTime = now;

      setStripePrices(prices);
      setError(null);
    } catch (err) {
      console.warn("Failed to load Stripe prices, using fallback prices:", err);
      setError("Using fallback pricing");
      // Используем базовые цены как fallback
      setStripePrices(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStripePrices();
  }, []);

  // *** ОБРАБОТАННЫЕ ПЛАНЫ С АКТУАЛЬНЫМИ ЦЕНАМИ ***
  const plans = useMemo(() => {
    if (loading) return [];

    return Object.keys(PLAN_CONFIGS)
      .map((planKey) => {
        const config = PLAN_CONFIGS[planKey];

        let oneTimePrice =
          typeof config.basePrice === "number" ? config.basePrice : 0;
        let subscriptionPrice =
          typeof config.subscriptionPrice === "number"
            ? config.subscriptionPrice
            : 0;

        if (stripePrices && stripePrices[planKey]) {
          const stripePlan = stripePrices[planKey];
          if (typeof stripePlan.oneTimePrice === "number") {
            oneTimePrice = stripePlan.oneTimePrice;
          }
          if (typeof stripePlan.subscriptionPrice === "number") {
            subscriptionPrice = stripePlan.subscriptionPrice;
          }

          const primaryPaid = VALID_PAID_PLANS[0];
          if (
            planKey === primaryPaid &&
            oneTimePrice > 0 &&
            subscriptionPrice >= oneTimePrice
          ) {
            console.warn(
              `${primaryPaid}: Stripe subscription should be below one-time; check Stripe prices`,
              { oneTimePrice, subscriptionPrice },
            );
          }
        }

        const subscriptionSavings = oneTimePrice - subscriptionPrice;
        const subscriptionSavingsPercent =
          oneTimePrice > 0
            ? Math.round((subscriptionSavings / oneTimePrice) * 100)
            : 0;

        const {
          stripeOneTimePriceId: _oid,
          stripeSubscriptionPriceId: _sid,
          ...planRest
        } = config;

        return {
          ...planRest,
          oneTimePrice,
          subscriptionPrice,
          subscriptionSavings,
          subscriptionSavingsPercent,
          isVisible: config.isVisible(user, totalSpent),
          isPricingFromStripe: !!(stripePrices && stripePrices[planKey]),
        };
      })
      .filter((plan) => plan.isVisible);
  }, [stripePrices, loading, user, totalSpent]);

  // *** ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ ***
  const getPlanByName = (planName) => {
    return plans.find((plan) => plan.name === planName);
  };

  const getVisiblePlans = () => {
    return plans;
  };

  const getPlanPrice = (planName, isSubscription = false) => {
    const plan = getPlanByName(planName);
    if (!plan) return 0;
    return isSubscription ? plan.subscriptionPrice : plan.oneTimePrice;
  };

  const getPlanSavings = (planName) => {
    const plan = getPlanByName(planName);
    if (!plan) return { amount: 0, percent: 0 };
    return {
      amount: plan.subscriptionSavings,
      percent: plan.subscriptionSavingsPercent,
    };
  };

  const refreshPrices = () => {
    stripePricesCache = null;
    stripePricesCacheTime = null;
    setLoading(true);
    loadStripePrices();
  };

  return {
    plans,
    loading,
    error,
    getPlanByName,
    getVisiblePlans,
    getPlanPrice,
    getPlanSavings,
    refreshPrices,
    stripePricesStatus: {
      hasStripePrices: !!stripePrices,
      lastUpdated: stripePricesCacheTime,
      cacheAge: stripePricesCacheTime
        ? Date.now() - stripePricesCacheTime
        : null,
    },
  };
};

// *** ХЕЛПЕРЫ ДЛЯ СОВМЕСТИМОСТИ ***
export const getAvailablePlans = (user = null, totalSpent = 0) => {
  return Object.keys(PLAN_CONFIGS)
    .map((planKey) => {
      const config = PLAN_CONFIGS[planKey];
      const {
        stripeOneTimePriceId: _o,
        stripeSubscriptionPriceId: _s,
        ...rest
      } = config;
      return {
        name: rest.name,
        price: typeof rest.basePrice === "number" ? rest.basePrice : 0,
        limit: rest.limit,
        description: rest.description,
        isVisible: rest.isVisible(user, totalSpent),
      };
    })
    .filter((plan) => plan.isVisible);
};

export { VALID_PAID_PLANS } from "../config/billingPlans";

export default usePricingPlans;
